import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Brain, ArrowRight, Layers, 
  Shield, Users, Target, Zap, CheckCircle, ChevronRight
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';
import api, { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

// ============================================
// HERO SECTION - Corporate Style
// ============================================
const HeroSection = ({ settings }) => {
  const { t } = useLanguage();

  const logoUrl = settings?.logo_url ? 
    (settings.logo_url.startsWith('http') ? settings.logo_url : `${API_URL}${settings.logo_url}`) : 
    null;

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-bms-light via-white to-blue-50 overflow-hidden"
    >
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100 rounded-full opacity-50 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-50 rounded-full opacity-50 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-6">
              {settings?.logo_visible && logoUrl ? (
                <img src={logoUrl} alt={settings?.site_name} className="h-6 w-auto" />
              ) : (
                <Brain className="w-5 h-5 text-bms-primary" />
              )}
              <span className="text-sm font-medium text-bms-primary">{settings?.site_name || 'BMS'}</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-bms-text mb-6 leading-tight">
              {t.hero.title1}
              <span className="block text-bms-primary">{t.hero.title2}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-bms-text-light mb-8 leading-relaxed max-w-xl">
              {t.hero.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/iletisim"
                  data-testid="cta-proje-baslat"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg font-semibold 
                           transition-all duration-300 shadow-blue hover:shadow-blue-lg"
                >
                  {t.hero.cta1}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/hakkimizda"
                  data-testid="cta-biz-kimiz"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-gray-200 hover:border-bms-primary text-bms-text hover:text-bms-primary rounded-lg font-semibold 
                           transition-all duration-300"
                >
                  {t.hero.cta2}
                </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Right - Stats/Features */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-corporate-xl p-8 border border-gray-100">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { icon: Shield, label: t.brand.s3, value: "100%", id: "security" },
                    { icon: Users, label: "Mutlu Müşteri", value: "50+", id: "clients" },
                    { icon: Target, label: "Proje", value: "20+", id: "projects" },
                    { icon: Zap, label: t.brand.s1, value: "7/24", id: "support" },
                  ].map((stat) => (
                    <div key={stat.id} className="text-center p-4 rounded-xl bg-bms-light">
                      <stat.icon className="w-8 h-8 text-bms-primary mx-auto mb-2" />
                      <div className="text-2xl font-bold text-bms-text">{stat.value}</div>
                      <div className="text-sm text-bms-text-muted">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SERVICES SECTION - Corporate Style
// ============================================
const ServicesSection = ({ services }) => {
  const { t, language } = useLanguage();

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="w-6 h-6" /> : <Layers className="w-6 h-6" />;
  };

  return (
    <section data-testid="services-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="text-bms-primary font-semibold text-sm uppercase tracking-wider">Hizmetlerimiz</span>
            <h2 className="text-3xl md:text-4xl font-bold text-bms-text mt-2 mb-4">
              {t.services.title}
            </h2>
            <p className="text-bms-text-light max-w-2xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 6).map((service, index) => {
            const title = language === 'tr' ? service.title_tr : service.title_en;
            const description = language === 'tr' ? service.short_desc_tr : service.short_desc_en;
            
            return (
              <ScrollReveal key={service.id || index} delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="corporate-card p-6 h-full"
                >
                  <div className="icon-container mb-4">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-lg font-semibold text-bms-text mb-2">{title}</h3>
                  <p className="text-bms-text-light text-sm mb-4 line-clamp-3">{description}</p>
                  <Link 
                    to={`/hizmetler/${service.slug}`}
                    className="inline-flex items-center gap-1 text-bms-primary text-sm font-medium hover:gap-2 transition-all"
                  >
                    {t.services.detailBtn}
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              </ScrollReveal>
            );
          })}
        </div>
        
        {services.length > 6 && (
          <div className="text-center mt-12">
            <Link 
              to="/hizmetler"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-bms-primary text-bms-primary hover:bg-bms-primary hover:text-white rounded-lg font-medium transition-all"
            >
              {t.services.allServices}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================
// PROCESS SECTION - Corporate Style
// ============================================
const ProcessSection = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="process-section" className="py-24 bg-bms-light">
      <div className="max-w-7xl mx-auto px-6">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="text-bms-primary font-semibold text-sm uppercase tracking-wider">Sürecimiz</span>
            <h2 className="text-3xl md:text-4xl font-bold text-bms-text mt-2 mb-4">
              {t.process.title} <span className="text-bms-primary">{t.process.titleHighlight}</span>
            </h2>
            <p className="text-bms-text-light max-w-2xl mx-auto">
              {t.process.subtitle}
            </p>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.process.steps.map((step) => (
            <ScrollReveal key={step.number} delay={parseInt(step.number) * 0.1}>
              <motion.div
                whileHover={{ y: -4 }}
                className="bg-white rounded-xl p-6 border border-gray-100 shadow-corporate h-full"
              >
                <div className="text-3xl font-bold text-bms-primary mb-3">{step.number}</div>
                <h3 className="text-lg font-semibold text-bms-text mb-2">{step.title}</h3>
                <p className="text-bms-text-light text-sm">{step.desc}</p>
              </motion.div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// WHY BMS SECTION - Corporate Style
// ============================================
const WhyBmsSection = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="why-bms-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal>
            <div>
              <span className="text-bms-primary font-semibold text-sm uppercase tracking-wider">Neden Biz?</span>
              <h2 className="text-3xl md:text-4xl font-bold text-bms-text mt-2 mb-4">
                {t.why.title} <span className="text-bms-primary">{t.why.titleHighlight}</span>
              </h2>
              <p className="text-bms-text-light mb-8">
                {t.why.subtitle}
              </p>
              
              <div className="space-y-4">
                {t.why.reasons.map((reason) => (
                  <motion.div
                    key={reason}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-bms-primary flex-shrink-0 mt-0.5" />
                    <span className="text-bms-text">{reason}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/hakkimizda"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg font-medium transition-all"
              >
                Daha Fazla Bilgi
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delay={0.3}>
            <div className="relative">
              <div className="bg-gradient-to-br from-bms-primary to-bms-primary-light rounded-2xl p-8 text-white">
                <Brain className="w-16 h-16 mb-6 opacity-80" />
                <h3 className="text-2xl font-bold mb-4">Profesyonel Çözümler</h3>
                <p className="text-blue-100 mb-6">
                  İşletmeniz için özel olarak tasarlanmış, ölçeklenebilir ve güvenli yazılım çözümleri sunuyoruz.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">10+</div>
                    <div className="text-sm text-blue-100">Yıl Deneyim</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">20+</div>
                    <div className="text-sm text-blue-100">Proje</div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

// ============================================
// CTA SECTION - Corporate Style
// ============================================
const FinalCtaSection = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="final-cta-section" className="py-24 bg-gradient-to-r from-bms-primary to-bms-primary-light">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <ScrollReveal>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t.cta.title1}<br />
            {t.cta.title2}
          </h2>
          
          <p className="text-blue-100 mb-10 text-lg max-w-2xl mx-auto">
            {t.cta.subtitle}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/iletisim"
                data-testid="cta-projenizi-konusalim"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-bms-primary hover:bg-gray-50 rounded-lg font-semibold 
                         transition-all duration-300 shadow-lg"
              >
                {t.cta.btn1}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/iletisim"
                data-testid="cta-iletisime-gec"
                className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-lg font-semibold 
                         transition-all duration-300"
              >
                {t.cta.btn2}
              </Link>
            </motion.div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

// ============================================
// HOME PAGE
// ============================================
const Home = () => {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, settingsRes] = await Promise.all([
          api.get('/services?active_only=true'),
          api.get('/settings')
        ]);
        setServices(servicesRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      <SEO 
        title={null}
        description="BrightMind Software - Akıllı sistemler, gerçek çözümler. Kurumsal yazılım, yapay zeka ve dijital dönüşüm çözümleri."
        keywords="yazılım, kurumsal yazılım, yapay zeka, dijital dönüşüm, web geliştirme, mobil uygulama, BrightMind"
      />
      <HeroSection settings={settings} />
      <ServicesSection services={services} />
      <ProcessSection />
      <WhyBmsSection />
      <FinalCtaSection />
    </div>
  );
};

export default Home;
